import {
  Icon
} from "./chunk-7IEFGXFA.js";

// src/components/canvas/lightbox/ImageLightbox.tsx
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useCallback, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function ImageLightbox({
  images,
  initialIndex = 0,
  isOpen,
  onClose
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);
  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => prev > 0 ? prev - 1 : images.length - 1);
  }, [images.length]);
  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => prev < images.length - 1 ? prev + 1 : 0);
  }, [images.length]);
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          handlePrevious();
          break;
        case "ArrowRight":
          handleNext();
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handlePrevious, handleNext, onClose]);
  if (!isOpen) return null;
  const currentImage = images[currentIndex];
  const showNavigation = images.length > 1;
  return /* @__PURE__ */ jsx(Dialog.Root, { open: isOpen, onOpenChange: (open) => {
    if (!open) onClose();
  }, children: /* @__PURE__ */ jsx(Dialog.Portal, { children: /* @__PURE__ */ jsx(Dialog.Content, { asChild: true, "aria-describedby": void 0, children: /* @__PURE__ */ jsxs(
    "div",
    {
      className: "fixed inset-0 z-[9999] flex items-center justify-center bg-black border-2 border-collapse rounded-lg ",
      style: { pointerEvents: "auto" },
      onClick: onClose,
      onMouseDown: (e) => e.stopPropagation(),
      onMouseMove: (e) => e.stopPropagation(),
      onMouseUp: (e) => e.stopPropagation(),
      onWheel: (e) => e.stopPropagation(),
      onTouchStart: (e) => e.stopPropagation(),
      onTouchMove: (e) => e.stopPropagation(),
      onTouchEnd: (e) => e.stopPropagation(),
      children: [
        /* @__PURE__ */ jsx(Dialog.Title, { className: "sr-only", children: "Image preview" }),
        showNavigation && /* @__PURE__ */ jsxs("div", { className: "absolute top-4 left-4 z-10 rounded-full bg-white/10 px-4 py-2 text-sm text-white backdrop-blur-sm", children: [
          currentIndex + 1,
          " / ",
          images.length
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            className: "absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20",
            onClick: onClose,
            "aria-label": "Close lightbox",
            children: /* @__PURE__ */ jsx(Icon, { name: "x", className: "h-5 w-5" })
          }
        ),
        showNavigation && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              className: "absolute top-1/2 left-4 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 disabled:opacity-50",
              onClick: (e) => {
                e.stopPropagation();
                handlePrevious();
              },
              "aria-label": "Previous image",
              children: /* @__PURE__ */ jsx(Icon, { name: "chevron-left", className: "h-6 w-6" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              className: "absolute top-1/2 right-4 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 disabled:opacity-50",
              onClick: (e) => {
                e.stopPropagation();
                handleNext();
              },
              "aria-label": "Next image",
              children: /* @__PURE__ */ jsx(Icon, { name: "chevron-right", className: "h-6 w-6" })
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            className: "absolute bottom-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20",
            onClick: async (e) => {
              e.stopPropagation();
              try {
                const imageUrl = currentImage.url;
                if (!imageUrl) return;
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = currentImage.alt || `image-${currentIndex + 1}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (err) {
                console.error("Failed to download image:", err);
              }
            },
            "aria-label": "Download image",
            children: /* @__PURE__ */ jsx(Icon, { name: "download", className: "h-5 w-5" })
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "relative bg-black shadow-float flex items-center justify-center",
            style: {
              padding: "5px",
              borderRadius: "14px",
              height: "100%",
              width: "100%"
              // maxHeight: 'calc(100vh - 120px)',
              // maxWidth: 'calc(100vw - 120px)',
            },
            onClick: (e) => e.stopPropagation(),
            children: /* @__PURE__ */ jsx(
              "div",
              {
                className: "relative flex items-center justify-center overflow-hidden rounded-sm",
                style: {
                  maxHeight: "75%",
                  maxWidth: "75%"
                },
                children: /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: currentImage.url,
                    alt: currentImage.alt || `Image ${currentIndex + 1}`,
                    className: "h-full w-full select-none object-contain",
                    draggable: false
                  }
                )
              }
            )
          }
        )
      ]
    }
  ) }) }) });
}

export {
  ImageLightbox
};
//# sourceMappingURL=chunk-I3CRLQRZ.js.map